import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import {
  LayoutDashboard, Package, ShoppingCart, Users, Settings,
  BarChart3, Tag, Bell, LogOut, Menu, X, ChevronRight,
  Shield, Truck, UserCog, Activity
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const { admin, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    if (loggingOut) return; // Prevent multiple clicks
    
    try {
      setLoggingOut(true);
      console.log('Logging out...');
      await logout();
      console.log('Logout successful, navigating to login page...');
      navigate('/staff/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigation even if logout fails
      navigate('/staff/login', { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  const menuItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Products', path: '/admin/products', icon: Package },
    { name: 'Orders', path: '/admin/orders', icon: ShoppingCart },
    { name: 'Customers', path: '/admin/customers', icon: Users },
    { name: 'Analytics', path: '/admin/analytics', icon: BarChart3 },
    { name: 'Coupons', path: '/admin/coupons', icon: Tag },
    { name: 'Warehouses', path: '/admin/shipping', icon: Truck },
    { name: 'Managers', path: '/admin/warehouse-managers', icon: Shield },
    { name: 'Staff', path: '/admin/staff', icon: UserCog },
    { name: 'Activity', path: '/admin/activity', icon: Activity },
    // REMOVED: SKU System, Inventory Units, and Reviews
    // { name: 'SKUs', path: '/admin/skus', icon: Hash },
    // { name: 'Units', path: '/admin/inventory-units', icon: Boxes },
    // { name: 'Reviews', path: '/admin/reviews', icon: MessageSquare },
    { name: 'Notifications', path: '/admin/notifications', icon: Bell },
    { name: 'Settings', path: '/admin/settings', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50 font-sans italic">
      
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-slate-950 border-r border-slate-800 transition-all duration-300 z-40 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo Header */}
        <div className="h-20 border-b border-slate-800 flex items-center justify-between px-6">
          {sidebarOpen ? (
            <>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl flex items-center justify-center shadow-lg">
                  <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <h1 className="text-sm font-black text-white uppercase tracking-tighter leading-none">
                    ADMIN
                  </h1>
                  <p className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">Portal</p>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="w-8 h-8 bg-slate-800/50 hover:bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              onClick={() => setSidebarOpen(true)}
              className="w-10 h-10 bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl flex items-center justify-center shadow-lg mx-auto"
            >
              <Menu className="w-5 h-5 text-white" strokeWidth={2.5} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-160px)]">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 h-12 rounded-xl transition-all group ${
                isActive(item.path)
                  ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/20'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={2.5} />
              {sidebarOpen && (
                <>
                  <span className="text-sm font-bold uppercase tracking-wide flex-1">{item.name}</span>
                  {isActive(item.path) && <ChevronRight className="w-4 h-4" />}
                </>
              )}
            </Link>
          ))}
        </nav>

        {/* Admin Profile */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-800 p-4">
          {sidebarOpen ? (
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center font-black text-white text-sm">
                {admin?.name?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold truncate">{admin?.name || 'Admin'}</p>
                <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider truncate">
                  {admin?.role || 'Administrator'}
                </p>
              </div>
            </div>
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center font-black text-white text-sm mx-auto mb-3">
              {admin?.name?.charAt(0)?.toUpperCase() || 'A'}
            </div>
          )}
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className={`w-full h-11 bg-red-600/10 hover:bg-red-600 border border-red-600/20 hover:border-red-600 text-red-400 hover:text-white rounded-xl transition-all flex items-center justify-center gap-2 font-bold text-sm uppercase tracking-wide ${
              !sidebarOpen && 'px-0'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <LogOut className="w-4 h-4" strokeWidth={2.5} />
            {sidebarOpen && <span>{loggingOut ? 'Logging out...' : 'Logout'}</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`transition-all duration-300 ${
          sidebarOpen ? 'ml-64' : 'ml-20'
        }`}
      >
        {/* Top Header */}
        <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-8 sticky top-0 z-30">
          <div>
            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">
              {menuItems.find((item) => isActive(item.path))?.name || 'Admin Portal'}
            </h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              BrightBazaar E-Commerce Platform
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Quick Stats */}
            <div className="hidden lg:flex items-center gap-4">
              <div className="text-right">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Today's Revenue</p>
                <p className="text-lg font-black text-slate-900">₹0</p>
              </div>
              <div className="w-px h-10 bg-slate-200"></div>
              <div className="text-right">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Orders</p>
                <p className="text-lg font-black text-slate-900">0</p>
              </div>
            </div>

            {/* Notifications */}
            <button className="relative w-11 h-11 bg-slate-100 hover:bg-slate-200 rounded-xl flex items-center justify-center text-slate-600 transition-colors">
              <Bell className="w-5 h-5" strokeWidth={2.5} />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-600 rounded-full"></span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
